import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { NgxCurrencyDirective } from 'ngx-currency';
import { MaskService } from '@shared/services/mask.service';
import { UtilService } from '@shared/services/util.service';

type MascaraContador = ReturnType<MaskService['getIntegerMask']> | ReturnType<MaskService['getDynamicDecimalMask']>;

/**
 * Contador em pílula "− valor +" com o valor digitável (máscara de quantidade) e centralizado.
 * Somente leitura exibe o valor sem edição e desabilita os botões.
 *
 * ```html
 * <app-contador-quantidade [value]="quantidade" (valueChange)="definir($event)" [mask]="mascara"
 *   [readonly]="somenteLeitura" [disabled]="ocupado" ariaLabel="Volumes recebidos"></app-contador-quantidade>
 * ```
 */
@Component({
  selector: 'app-contador-quantidade',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule, NgxCurrencyDirective],
  templateUrl: './contador-quantidade.component.html',
  styleUrl: './contador-quantidade.component.css',
})
export class ContadorQuantidadeComponent {
  @Input() value: number | null = null;
  @Input() step = 1;
  @Input() min = 0;
  @Input() readonly = false;
  @Input() disabled = false;
  /** Versão menor, para linhas de tabela. */
  @Input() compact = false;
  /** Rótulo acessível do campo. */
  @Input() ariaLabel = 'Quantidade';
  /** Complemento dos rótulos dos botões: "Diminuir <contexto>" / "Aumentar <contexto>". */
  @Input() ariaContexto = '';
  @Output() valueChange = new EventEmitter<number>();

  mascara: MascaraContador & { align: string };

  constructor(maskService: MaskService) {
    this.mascara = { ...maskService.getDynamicDecimalMask(null, false), align: 'center' };
  }

  /** Máscara do campo (padrão: quantidade decimal da empresa, sem negativo). */
  @Input() set mask(valor: MascaraContador | null | undefined) {
    if (valor) this.mascara = { ...valor, align: 'center' };
  }

  get rotuloDiminuir(): string {
    return this.ariaContexto ? `Diminuir ${this.ariaContexto}` : 'Diminuir';
  }

  get rotuloAumentar(): string {
    return this.ariaContexto ? `Aumentar ${this.ariaContexto}` : 'Aumentar';
  }

  get formatoLeitura(): string {
    return `1.0-${this.mascara.precision}`;
  }

  get podeDiminuir(): boolean {
    return !this.readonly && !this.disabled && (this.value ?? 0) > this.min;
  }

  alterar(sinal: 1 | -1): void {
    if (this.readonly || this.disabled) return;
    this.emitir((this.value ?? 0) + sinal * this.step);
  }

  digitar(valor: number | null): void {
    if (this.readonly) return;
    this.emitir(Number(valor) || 0);
  }

  private emitir(valor: number): void {
    this.value = Math.max(this.min, UtilService.arredondarAbnt(valor, this.mascara.precision));
    this.valueChange.emit(this.value);
  }
}
